<template>
    <div>
        <b-modal
        :id="modalId"
        :title="isEditing ? 'Editar Diagnóstico' : 'Nuevo Diagnóstico'"
        size="lg"
        @hidden="handleHidden"
        @show="loadFormData"
        no-close-on-backdrop
        scrollable
    >
        <b-form @submit.prevent="handleSubmit" :key="formKey">
            <b-row>
                <!-- Lista de Chequeo -->
                <b-col cols="12" md="6" class="mb-3">
                    <label for="check_list">
                        <i class="fa fa-check-square-o" aria-hidden="true"></i>
                        Lista de Chequeo <span class="text-danger">*</span>
                    </label>
                    <el-select
                        id="check_list"
                        v-model="form.check_lists_companies_id"
                        v-validate="'required'"
                        name="check_lists_companies_id"
                        data-vv-as="Lista de Chequeo"
                        placeholder="Seleccione una lista"
                        size="medium"
                        filterable
                        :disabled="loading"
                        :class="{
                            'is-invalid': errors.has(
                                'check_lists_companies_id'
                            ),
                        }"
                        style="width: 100%"
                    >
                        <el-option
                            v-for="checklist in safeCheckListCompany"
                            :key="checklist.id"
                            :label="checklist.check_list.name"
                            :value="checklist.id"
                        />
                    </el-select>
                    <small
                        v-if="errors.has('check_lists_companies_id')"
                        class="text-danger"
                    >
                        {{ errors.first("check_lists_companies_id") }}

                    </small>
                </b-col>

                <!-- Ubicación -->
                <b-col cols="12" md="6" class="mb-3">
                    <label for="location">
                        <i class="fa fa-map-marker" aria-hidden="true"></i>
                        Ubicación <span class="text-danger">*</span>
                    </label>
                    <tree-select
                        v-model="form.location_id"
                        v-validate="'required'"
                        name="location_id"
                        data-vv-as="Ubicación"
                        id="location"
                        :multiple="false"
                        :defaultExpandLevel="Infinity"
                        :options="treeWithChildrens"
                        placeholder="Seleccione ubicación"
                        :disabled="loading"
                    />
                    <small v-if="errors.has('location_id')" class="text-danger">
                        {{ errors.first("location_id") }}
                    </small>
                </b-col>

                <!-- Champion / Responsable principal -->
                <b-col cols="12" md="6" class="mb-3">
                    <label for="responsible">
                        <i class="fa fa-user"></i> Champion / Responsable
                        principal <span class="text-danger">*</span>
                    </label>
                    <div class="select-with-action">
                        <el-select
                            id="responsible"
                            v-model="form.responsible_id"
                            v-validate="'required'"
                            name="responsible_id"
                            data-vv-as="Champion / Responsable principal"
                            placeholder="Seleccione Champion / Responsable principal"
                            size="medium"
                            filterable
                            :disabled="loading"
                            :class="{ 'is-invalid': errors.has('responsible_id') }"
                        >
                            <el-option
                                v-for="worker in safeWorkers"
                                :key="worker.id"
                                :label="worker.fullName"
                                :value="worker.id"
                            />
                        </el-select>
                        <b-button
                            variant="primary"
                            size="sm"
                            class="ml-2"
                            :disabled="loading || !currentCompanyId"
                            @click="openWorkerModal('responsible')"
                        >
                            <i class="fa fa-plus"></i>
                        </b-button>
                    </div>
                    <small
                        v-if="errors.has('responsible_id')"
                        class="text-danger"
                    >
                        {{ errors.first("responsible_id") }}
                    </small>
                </b-col>

                <!-- Fecha Diagnóstico -->
                <b-col cols="12" md="6" class="mb-3">
                    <label for="diagnostic_date">
                        <i class="fa fa-calendar"></i> Fecha Diagnóstico
                        <span class="text-danger">*</span>
                    </label>
                    <el-date-picker
                        v-model="form.diagnostic_date"
                        v-validate="'required'"
                        name="diagnostic_date"
                        data-vv-as="Fecha Diagnóstico"
                        type="date"
                        placeholder="Seleccione fecha"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd"
                        size="medium"
                        :disabled="loading"
                        :class="{ 'is-invalid': errors.has('diagnostic_date') }"
                        style="width: 100%"
                    >
                    </el-date-picker>
                    <small
                        v-if="errors.has('diagnostic_date')"
                        class="text-danger"
                    >
                        {{ errors.first("diagnostic_date") }}
                    </small>
                </b-col>

                <!-- Fecha Seguimiento -->
                <b-col cols="12" md="6" class="mb-3">
                    <label for="follow_up_date">
                        <i class="fa fa-calendar"></i> Fecha Seguimiento
                    </label>
                    <el-date-picker
                        v-model="form.follow_up_date"
                        type="date"
                        placeholder="Seleccione fecha"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd"
                        size="medium"
                        :disabled="loading"
                        style="width: 100%"
                    >
                    </el-date-picker>
                    <small v-if="errors.follow_up_date" class="text-danger">
                        {{ errors.follow_up_date[0] }}
                    </small>
                </b-col>

                <!-- Estado (solo en edición) -->
                <b-col cols="12" md="6" class="mb-3" v-if="isEditing">
                    <label for="is_active"> </label>
                    <div class="mt-2">
                        <b-form-checkbox
                            id="is_active"
                            v-model="form.is_active"
                            name="is_active"
                            :disabled="loading"
                            :value="1"
                            :unchecked-value="0"
                            switch
                            size="lg"
                        >
                            <span class="ml-2">{{
                                form.is_active ? "Activo" : "Inactivo"
                            }}</span>
                        </b-form-checkbox>
                    </div>
                </b-col>

                <!-- Observaciones -->
                <b-col cols="12" class="mb-3">
                    <label for="observations">
                        <i class="fa fa-comment"></i> Observaciones
                    </label>
                    <b-form-textarea
                        id="observations"
                        v-model="form.observations"
                        placeholder="Ingrese observaciones..."
                        rows="3"
                        :disabled="loading"
                    ></b-form-textarea>
                    <small v-if="errors.observations" class="text-danger">
                        {{ errors.observations[0] }}
                    </small>
                </b-col>

                <!-- Roles y Workers -->
                <b-col
                    cols="12"
                    class="mb-3 mt-2"
                    v-if="rolesActives.length > 0"
                >
                    <div class="roles-header">
                        <label>
                            <i class="fa fa-users"></i> Asignación de Roles
                            <b-badge variant="info" class="ml-2">
                                {{ assignedRolesCount }} /
                                {{ rolesActives.length }} roles asignados
                            </b-badge>
                        </label>
                        <b-button
                            size="sm"
                            variant="primary"
                            :disabled="loading || !currentCompanyId"
                            @click="openWorkerModal('roles')"
                        >
                            <i class="fa fa-plus"></i> Nuevo trabajador
                        </b-button>
                    </div>
                    <div class="roles-container">
                        <div
                            v-for="role in rolesActives"
                            :key="role.id"
                            class="role-card"
                        >
                            <div class="role-header">
                                <span class="role-name">
                                    <i class="fa fa-user-circle-o"></i>
                                    {{ role.name }}
                                </span>
                                <b-badge
                                    :variant="
                                        getRoleWorkersCount(role.id) > 0
                                            ? 'success'
                                            : 'secondary'
                                    "
                                    pill
                                >
                                    {{ getRoleWorkersCount(role.id) }} asignados
                                </b-badge>
                            </div>

                            <!-- Selector para agregar workers -->
                            <div class="add-worker-section">
                                <el-select
                                    :value="null"
                                    @change="
                                        (value) =>
                                            addWorkerToRole(role.id, value)
                                    "
                                    placeholder="+ Agregar trabajador"
                                    size="small"
                                    filterable
                                    :disabled="loading"
                                    style="width: 100%"
                                >
                                    <el-option
                                        v-for="worker in getAvailableWorkersForRole(
                                            role.id
                                        )"
                                        :key="worker.id"
                                        :label="worker.fullName"
                                        :value="worker.id"
                                    />
                                </el-select>
                            </div>

                            <!-- Workers asignados como chips -->
                            <div
                                class="workers-chips mt-2"
                                v-if="getRoleWorkers(role.id).length > 0"
                            >
                                <div
                                    v-for="workerId in getRoleWorkers(role.id)"
                                    :key="workerId"
                                    class="worker-chip"
                                >
                                    <span
                                        ><i class="fa fa-user"></i>
                                        {{ getWorkerName(workerId) }}</span
                                    >
                                    <i
                                        class="fa fa-times-circle remove-icon"
                                        @click="
                                            removeWorkerFromRole(
                                                role.id,
                                                workerId
                                            )
                                        "
                                        :title="'Quitar '"
                                    ></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </b-col>

                <!-- Documentos Requeridos -->
                <b-col
                    cols="12"
                    v-if="availableDocuments.length > 0"
                    class="mb-3"
                >
                    <label>
                        <i class="fa fa-file-text"></i> Documentos Requeridos
                        <b-badge variant="info" class="ml-2">
                            {{ form.documents_ids.length }} /
                            {{ availableDocuments.length }} seleccionados
                        </b-badge>
                    </label>
                    <div class="documents-container">
                        <div
                            v-for="document in availableDocuments"
                            :key="document.id"
                            class="document-item"
                        >
                            <div class="document-info">
                                <span class="document-name">{{
                                    document.name
                                }}</span>
                                <small
                                    v-if="document.description"
                                    class="text-muted d-block ml-4"
                                >
                                    {{ document.description }}
                                </small>
                            </div>
                            <b-form-checkbox
                                :checked="isDocumentSelected(document.id)"
                                @change="toggleDocument(document.id)"
                                :disabled="loading"
                                switch
                                size="lg"
                            >
                            </b-form-checkbox>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </b-form>

        <template #modal-footer>
            <b-button
                variant="secondary"
                @click="closeModal"
                :disabled="loading"
            >
                <i class="fa fa-times"></i> Cancelar
            </b-button>
            <b-button
                variant="primary"
                @click="handleSubmit"
                :disabled="loading"
            >
                <i class="fa fa-save"></i>
                {{ isEditing ? "Actualizar" : "Guardar" }}
            </b-button>
        </template>
    </b-modal>

    <b-modal
        :id="workerModalId"
        title="Crear trabajador"
        hide-footer
        size="lg"
        @hidden="handleWorkerModalHidden"
    >
        <FormWorker
            :company_id="currentCompanyId"
            :showAddPosition="true"
            :dni_id_required="false"
            module="DIAGNOSTICOS"
            @saveWorker="handleWorkerSave"
        />
    </b-modal>
    </div>
</template>

<script>
import TreeSelect from "@riophae/vue-treeselect";
import { mapActions, mapMutations, mapState } from "vuex";
import FormWorker from "../../../../views/workers/workers/components/FormWorker.vue";

export default {
    name: "FormDiagnostic",
    components: { TreeSelect, FormWorker },
    props: {
        modalId: {
            type: String,
            default: "modal-diagnostic",
        },
        companyId: {
            type: Number,
            default: null,
        },
        diagnosticId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            formKey: 0,
            workerModalId: "modal-diagnostic-worker",
            workerModalContext: null,
            form: {
                company_id: null,
                check_lists_companies_id: null,
                location_id: null,
                responsible_id: null,
                diagnostic_date: null,
                follow_up_date: null,
                observations: null,
                is_active: 1,
                documents_ids: [],
                roles: [], // { role_id: 1, workers_ids: [1, 2, 3] }
            },
        };
    },
    computed: {
        ...mapState("location", ["treeWithChildrens"]),
        ...mapState("skudo/checklist", ["checkListCompany"]),
        ...mapState("trainings", ["workers"]),
        ...mapState("skudo/roles", ["rolesActives"]),
        ...mapState("workers", ["worker"]),

        isEditing() {
            return !!this.diagnosticId;
        },

        currentCompanyId() {
            return this.form.company_id || this.companyId || null;
        },

        safeCheckListCompany() {
            return Array.isArray(this.checkListCompany)
                ? this.checkListCompany
                : [];
        },

        safeWorkers() {
            if (!this.workers || !this.workers.length) return [];
            return this.workers.map((worker) => ({
                ...worker,
                fullName: `${worker.names || ""} ${
                    worker.surnames || ""
                }`.trim(),
            }));
        },

        availableDocuments() {
            if (!this.form.check_lists_companies_id) return [];

            const selectedChecklist = this.safeCheckListCompany.find(
                (cl) => cl.id === this.form.check_lists_companies_id
            );

            return selectedChecklist &&
                selectedChecklist.check_list &&
                selectedChecklist.check_list.documents
                ? selectedChecklist.check_list.documents
                : [];
        },

        assignedRolesCount() {
            return this.form.roles.filter(
                (r) => r.workers_ids && r.workers_ids.length > 0
            ).length;
        },
    },
    watch: {
        "form.check_lists_companies_id"(newVal) {
            if (newVal && !this.isEditing) {
                // Al crear: marcar todos los documentos por defecto
                this.form.documents_ids = this.availableDocuments.map(
                    (doc) => doc.id
                );
            } else if (!newVal) {
                // Si no hay checklist, limpiar documentos
                this.form.documents_ids = [];
            }
        },
    },
    methods: {
        ...mapActions("location", ["getTreeWithChildrens"]),
        ...mapActions("skudo/checklist", ["getCheckListCompany"]),
        ...mapActions("trainings", ["fetchWorkers"]),
        ...mapActions("skudo/diagnostics", [
            "createDiagnostic",
            "updateDiagnostic",
            "getDiagnosticById",
        ]),
        ...mapActions("skudo/roles", ["getAllActives"]),
        ...mapActions("workers", ["saveWorker"]),
        ...mapMutations("workers", ["resetWorker"]),

        toggleDocument(documentId) {
            const index = this.form.documents_ids.indexOf(documentId);
            if (index > -1) {
                // Si está seleccionado, quitarlo
                this.form.documents_ids.splice(index, 1);
            } else {
                // Si no está seleccionado, agregarlo
                this.form.documents_ids.push(documentId);
            }
        },

        isDocumentSelected(documentId) {
            return this.form.documents_ids.includes(documentId);
        },

        async loadFormData() {
            if (!this.companyId) {
                this.$alertError("Debe seleccionar una empresa primero");
                return;
            }

            this.loading = true;
            try {
                if (this.isEditing) {
                    // Cargar opciones primero para que los selects tengan datos
                    await Promise.all([
                        this.getTreeWithChildrens(this.companyId),
                        this.getCheckListCompany(this.companyId),
                        this.fetchWorkers(this.companyId),
                        this.getAllActives(),
                    ]);

                    // Esperar para que los componentes se rendericen
                    await new Promise((resolve) => setTimeout(resolve, 100));

                    // Cargar datos del diagnóstico
                    await this.loadDiagnosticData();
                    await this.$nextTick();
                } else {
                    // Modo creación
                    this.form.company_id = this.companyId;
                    await Promise.all([
                        this.getTreeWithChildrens(this.companyId),
                        this.getCheckListCompany(this.companyId),
                        this.fetchWorkers(this.companyId),
                        this.getAllActives(),
                    ]);
                }
            } catch (error) {
                this.$alertError("Error al cargar datos del formulario");
            } finally {
                this.loading = false;
            }
        },

        async loadDiagnosticData() {
            try {
                const response = await this.getDiagnosticById({
                    id: this.diagnosticId,
                    company_id: this.companyId,
                });
                const diagnostic = response.data.data || response.data;

                // Obtener company_id del diagnóstico o usar el de la prop
                const companyIdToSet =
                    diagnostic.company_id ||
                    (diagnostic.check_list_company &&
                        diagnostic.check_list_company.company_id) ||
                    this.companyId;

                // Usar $set para garantizar reactividad
                this.$set(this.form, "company_id", companyIdToSet);
                this.$set(
                    this.form,
                    "check_lists_companies_id",
                    diagnostic.check_lists_companies_id
                );
                this.$set(this.form, "location_id", diagnostic.location_id);
                this.$set(
                    this.form,
                    "responsible_id",
                    diagnostic.responsible_id
                );
                this.$set(
                    this.form,
                    "diagnostic_date",
                    diagnostic.diagnostic_date
                );
                this.$set(
                    this.form,
                    "follow_up_date",
                    diagnostic.follow_up_date
                );
                this.$set(this.form, "observations", diagnostic.observations);
                this.$set(this.form, "is_active", diagnostic.is_active);

                // Cargar documentos en edición
                if (
                    diagnostic.documents &&
                    Array.isArray(diagnostic.documents)
                ) {
                    this.$set(
                        this.form,
                        "documents_ids",
                        diagnostic.documents.map((doc) => doc.document_list_id)
                    );
                } else {
                    // Si no hay documentos guardados, marcar todos por defecto
                    this.$set(
                        this.form,
                        "documents_ids",
                        this.availableDocuments.map((doc) => doc.id)
                    );
                }

                // Cargar roles en edición
                if (diagnostic.roles && Array.isArray(diagnostic.roles)) {
                    const rolesFormatted = diagnostic.roles.map((role) => ({
                        role_id: role.role_id,
                        workers_ids: role.workers.map((w) => w.worker_id),
                    }));
                    this.$set(this.form, "roles", rolesFormatted);
                } else {
                    this.$set(this.form, "roles", []);
                }

                // Incrementar formKey para forzar re-render
                this.formKey++;
            } catch (error) {
                this.$alertError("Error al cargar el diagnóstico");
            }
        },

        async handleSubmit() {
            // Validar formulario
            const isValid = await this.$validator.validateAll();
            if (!isValid) {
                return;
            }

            this.loading = true;

            try {
                const payload = { ...this.form };

                if (this.isEditing) {
                    await this.updateDiagnostic({
                        id: this.diagnosticId,
                        data: payload,
                    });
                } else {
                    await this.createDiagnostic(payload);
                }

                this.$alertSuccess(
                    this.isEditing
                        ? "Diagnóstico actualizado exitosamente"
                        : "Diagnóstico creado exitosamente"
                );
                this.$emit("saved");
                this.closeModal();
            } catch (error) {
                if (
                    error.response &&
                    error.response.data &&
                    error.response.data.errors
                ) {
                    const backendErrors = error.response.data.errors;
                    Object.keys(backendErrors).forEach((field) => {
                        this.$validator.errors.add({
                            field: field,
                            msg: backendErrors[field][0],
                        });
                    });
                } else {
                    const message =
                        error.response &&
                        error.response.data &&
                        error.response.data.message
                            ? error.response.data.message
                            : "Error al guardar el diagnóstico";
                    this.$alertError(message);
                }
            } finally {
                this.loading = false;
            }
        },

        getRoleWorkers(roleId) {
            const role = this.form.roles.find((r) => r.role_id === roleId);
            return role ? role.workers_ids : [];
        },

        getRoleWorkersCount(roleId) {
            return this.getRoleWorkers(roleId).length;
        },

        getWorkerName(workerId) {
            const worker = this.safeWorkers.find((w) => w.id === workerId);
            return worker ? worker.fullName : "Desconocido";
        },

        getAvailableWorkersForRole(roleId) {
            const assignedWorkers = this.getRoleWorkers(roleId);
            return this.safeWorkers.filter(
                (w) => !assignedWorkers.includes(w.id)
            );
        },

        addWorkerToRole(roleId, workerId) {
            if (!workerId) return;

            // Buscar si ya existe el rol
            const roleIndex = this.form.roles.findIndex(
                (r) => r.role_id === roleId
            );

            if (roleIndex >= 0) {
                // Si existe, agregar el worker al array
                if (
                    !this.form.roles[roleIndex].workers_ids.includes(workerId)
                ) {
                    this.form.roles[roleIndex].workers_ids.push(workerId);
                }
            } else {
                // Si no existe, crear el rol con el worker
                this.form.roles.push({
                    role_id: roleId,
                    workers_ids: [workerId],
                });
            }
        },

        removeWorkerFromRole(roleId, workerId) {
            const roleIndex = this.form.roles.findIndex(
                (r) => r.role_id === roleId
            );

            if (roleIndex >= 0) {
                const workerIndex =
                    this.form.roles[roleIndex].workers_ids.indexOf(workerId);
                if (workerIndex >= 0) {
                    this.form.roles[roleIndex].workers_ids.splice(
                        workerIndex,
                        1
                    );

                    // Si no quedan workers, remover el rol del array
                    if (this.form.roles[roleIndex].workers_ids.length === 0) {
                        this.form.roles.splice(roleIndex, 1);
                    }
                }
            }
        },

        resetForm() {
            this.form = {
                company_id: null,
                check_lists_companies_id: null,
                location_id: null,
                responsible_id: null,
                diagnostic_date: null,
                follow_up_date: null,
                observations: null,
                is_active: 1,
                documents_ids: [],
                roles: [],
            };
            // Limpiar errores de validación
            this.$nextTick(() => {
                this.$validator.reset();
            });
        },

        handleHidden() {
            this.resetForm();
            this.$emit("hidden");
        },

        closeModal() {
            this.$bvModal.hide(this.modalId);
        },

        openWorkerModal(context = null) {
            if (!this.currentCompanyId) {
                this.$alertWarning("Seleccione una empresa primero");
                return;
            }
            this.workerModalContext = context;
            this.resetWorker();
            this.$bvModal.show(this.workerModalId);
        },

        async handleWorkerSave() {
            if (!this.currentCompanyId) {
                this.$alertWarning("Seleccione una empresa primero");
                return;
            }

            try {
                await this.saveWorker(this.currentCompanyId);
                await this.fetchWorkers(this.currentCompanyId);
                this.$bvModal.hide(this.workerModalId);
            } catch (error) {
                this.$alertError("Error al crear el trabajador");
            }
        },

        handleWorkerModalHidden() {
            this.workerModalContext = null;
        },
    },
};
</script>

<style scoped>
/* ... */
.text-danger {
    color: #dc3545;
}

::v-deep .vue-treeselect__no-children-tip {
    display: none !important;
}

::v-deep .vue-treeselect__no-children-tip .vue-treeselect__icon {
    display: none !important;
}

.vue-treeselect__control {
    min-height: 30px !important;
    font-size: 14px !important;
}

.imagen-checkbox {
    box-shadow: 0 0 3px #b8b8b8;
}

.is-checked .imagen-checkbox {
    box-shadow: 0px 1px 10px #097b83;
    border: 1px solid #097b83;
}

.checkbox-imagen .el-radio__input {
    display: none;
}

/* Estilos para roles y workers */
.roles-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 14px;
    margin-top: 10px;
}

.roles-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.role-card {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 14px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.role-card:hover {
    border-color: #17a2b8;
    box-shadow: 0 2px 6px rgba(23, 162, 184, 0.15);
}

.role-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e9ecef;
}

.role-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: #495057;
    display: flex;
    align-items: center;
    gap: 6px;
}

.role-name i {
    font-size: 1.1rem;
}

.select-with-action {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: end;
}

.select-with-action .el-select {
    flex: 1;
}

.select-with-action .btn {
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
}

.workers-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 0;
    min-height: 32px;
}

.worker-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #17a2b8;
    color: white;
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 0.75rem;
    font-weight: 500;
    transition: all 0.2s ease;
    flex: 0 0 calc(50% - 3px);
    max-width: calc(50% - 3px);
}

.worker-chip:hover {
    background: #138496;
}

.worker-chip span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
}

.worker-chip span i {
    flex-shrink: 0;
}

.remove-icon {
    cursor: pointer;
    font-size: 0.95rem;
    transition: all 0.2s ease;
    opacity: 0.85;
}

.remove-icon:hover {
    opacity: 1;
    transform: scale(1.15);
}

.add-worker-section {
    margin-top: 10px;
}

.add-worker-section ::v-deep .el-input__inner {
    border-radius: 6px;
    border: 1px dashed #ced4da;
    transition: all 0.2s ease;
    background-color: #fafafa;
    font-size: 0.85rem;
    padding: 8px 12px;
}

.add-worker-section ::v-deep .el-input__inner:hover {
    border-color: #17a2b8;
    background-color: white;
}

.add-worker-section ::v-deep .el-input__inner:focus {
    border-color: #17a2b8;
    border-style: solid;
    background-color: white;
    box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.15);
}

.add-worker-section ::v-deep .el-input__icon {
    color: #6c757d;
}

/* Responsive para roles */
@media (max-width: 768px) {
    .roles-container {
        grid-template-columns: 1fr;
    }

    .worker-chip {
        flex: 0 0 calc(50% - 3px);
        max-width: calc(50% - 3px);
        font-size: 0.7rem;
        padding: 4px 8px;
    }

    .worker-chip span {
        gap: 3px;
    }
}

/* Estilos para documentos */
.documents-container {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 10px;
    max-height: 400px;
    overflow-y: auto;
    background-color: #f8f9fa;
}

.document-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    margin-bottom: 8px;
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    transition: all 0.2s ease;
}

.document-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: #007bff;
}

.document-item:last-child {
    margin-bottom: 0;
}

.document-info {
    flex: 1;
    min-width: 0;
}

.document-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
    display: block;
}

.document-info small {
    font-size: 0.8rem;
    line-height: 1.4;
}
</style>
